import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { PhoneInput } from '@/components/PhoneInput';
import { PasswordInput } from '@/components/PasswordInput';
import { ResendVerification } from '@/components/ResendVerification';
import { AuthShell } from '@/layouts/AuthShell';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { homePathFor } from '@/lib/home-path';

const EMPTY = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: '',
  membershipNumber: '',
  password: '',
  confirmPassword: '',
};

export default function Register() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key: keyof typeof EMPTY) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);

    try {
      await api.post('/auth/register', {
        ...form,
        middleName: form.middleName || undefined,
        acceptedTerms,
        acceptedTermsVersion: 'v1.0',
      });
      setDone(true);
    } catch (caught) {
      if (caught instanceof ApiError) {
        // The server returns per-field messages; surface them inline rather
        // than as one opaque banner.
        setFieldErrors(caught.fieldErrors);
        setError(caught.fields.length ? null : caught.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Someone already signed in does not need an account.
  if (!authLoading && user && !done) return <Navigate to={homePathFor(user)} replace />;

  if (done) {
    return (
      <AuthShell title="Check your email" description="Your account is almost ready.">
        <Card className="p-6 sm:p-8">
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckCircle2 size={22} />
          </span>
          <p className="mb-6 leading-relaxed text-foreground-secondary">
            We have sent a verification link to{' '}
            <strong className="text-foreground">{form.email}</strong>. Click it to activate your
            account, then sign in. The link is valid for 24 hours.
          </p>
          <Button full size="lg" onClick={() => navigate('/login')}>
            Go to sign in
          </Button>

          <div className="mt-4 border-t border-border pt-4">
            <p className="mb-3 text-xs leading-relaxed text-foreground-secondary">
              Nothing after a few minutes? Check your spam folder, then request another link.
            </p>
            <ResendVerification email={form.email} />
          </div>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      wide
      title="Create your account"
      description="Already a UNIFEMGA member? Set up your account to apply for an interest-free loan."
      footer={
        <p className="text-center text-sm text-foreground-secondary">
          Already registered?{' '}
          <Link to="/login" className="font-bold text-primary hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      <Card className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="firstName" error={fieldErrors['firstName']} required>
              <Input id="firstName" value={form.firstName} onChange={set('firstName')} required invalid={Boolean(fieldErrors['firstName'])} />
            </Field>
            <Field label="Last name" htmlFor="lastName" error={fieldErrors['lastName']} required>
              <Input id="lastName" value={form.lastName} onChange={set('lastName')} required invalid={Boolean(fieldErrors['lastName'])} />
            </Field>
          </div>

          <Field label="Middle name" htmlFor="middleName" error={fieldErrors['middleName']}>
            <Input id="middleName" value={form.middleName} onChange={set('middleName')} invalid={Boolean(fieldErrors['middleName'])} />
          </Field>

          <Field label="Email address" htmlFor="email" error={fieldErrors['email']} required>
            <Input id="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} required invalid={Boolean(fieldErrors['email'])} />
          </Field>

          <Field
            label="Phone number"
            htmlFor="phone"
            error={fieldErrors['phone']}
            hint="Nigerian mobile, e.g. 0916 848 4980"
            required
          >
            <PhoneInput
              id="phone"
              value={form.phone}
              onChange={(phone) => setForm((current) => ({ ...current, phone }))}
              required
              invalid={Boolean(fieldErrors['phone'])}
            />
          </Field>

          <Field
            label="UNIFEMGA membership number"
            htmlFor="membershipNumber"
            error={fieldErrors['membershipNumber']}
            hint="As issued by UNIFEMGA, e.g. UNF/2019/0142. Ask the secretariat if you do not know yours."
            required
          >
            <Input
              id="membershipNumber"
              value={form.membershipNumber}
              onChange={set('membershipNumber')}
              required
              invalid={Boolean(fieldErrors['membershipNumber'])}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            error={fieldErrors['password']}
            hint="At least 12 characters, with upper case, lower case and a number."
            required
          >
            <PasswordInput id="password" autoComplete="new-password" value={form.password} onChange={set('password')} required invalid={Boolean(fieldErrors['password'])} />
          </Field>

          <Field label="Confirm password" htmlFor="confirmPassword" error={fieldErrors['confirmPassword']} required>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={set('confirmPassword')}
              required
              invalid={Boolean(fieldErrors['confirmPassword'])}
            />
          </Field>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-muted p-4">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
              required
            />
            <span className="text-sm text-foreground-secondary">
              I accept the{' '}
              <Link to="/terms" className="font-semibold text-primary hover:underline">
                terms and conditions
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="font-semibold text-primary hover:underline">
                privacy policy
              </Link>
              .
            </span>
          </label>
          {fieldErrors['acceptedTerms'] && (
            <p role="alert" className="text-xs font-medium text-danger">
              {fieldErrors['acceptedTerms']}
            </p>
          )}

          <Button type="submit" full size="lg" loading={loading} disabled={!acceptedTerms}>
            Create account <ArrowRight size={16} />
          </Button>

        </form>
      </Card>
    </AuthShell>
  );
}
